import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  TURNSTILE_SITE_KEY,
  WAITLIST_API_URL,
} from "../config";

type SubmitState = "idle" | "submitting" | "success" | "error";

export function BetaPage() {
  const [state, setState] = useState<SubmitState>("idle");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
  const turnstileHost = useRef<HTMLDivElement>(null);
  const turnstileWidget = useRef<string | null>(null);

  useEffect(() => {
    let stopped = false;
    if (!document.getElementById("turnstile-script")) {
      const script = document.createElement("script");
      script.id = "turnstile-script";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const tryRender = () => {
      if (stopped || turnstileWidget.current || !turnstileHost.current) return;
      if (!window.turnstile) return;
      setTurnstileUnavailable(false);
      turnstileWidget.current = window.turnstile.render(turnstileHost.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: setTurnstileToken,
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => {
          setTurnstileToken("");
          setTurnstileUnavailable(true);
        },
      });
    };
    const timer = window.setInterval(tryRender, 150);
    const unavailableTimer = window.setTimeout(() => {
      if (!turnstileWidget.current) setTurnstileUnavailable(true);
    }, 8_000);
    tryRender();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.clearTimeout(unavailableTimer);
      if (turnstileWidget.current && window.turnstile) {
        window.turnstile.remove(turnstileWidget.current);
      }
      turnstileWidget.current = null;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const payload = {
      name: String(form.get("name") ?? "").trim(),
      contact: String(form.get("contact") ?? "").trim(),
      role: String(form.get("role") ?? "").trim(),
      setup: String(form.get("setup") ?? "").trim(),
      website: String(form.get("website") ?? "").trim(),
      turnstileToken,
    };

    try {
      const response = await fetch(WAITLIST_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Waitlist request failed");
      setState("success");
      formElement.reset();
    } catch {
      setState("error");
      setTurnstileToken("");
      if (turnstileWidget.current && window.turnstile) {
        window.turnstile.reset(turnstileWidget.current);
      }
    }
  }

  return (
    <main className="wrap demo-page beta-page">
      <header className="demo-hero rise rise-1">
        <span className="eyebrow">Early beta</span>
        <h1>Help us make KinSafe better on real family hardware.</h1>
        <p>
          We&apos;re looking for beta testers who can try the Windows app and
          tell us where it gets things right or wrong. Beta spots are limited.
        </p>
      </header>

      <section className="section beta-section">
        <div>
          {state === "success" ? (
            <div className="form-status form-success" role="status">
              <h2>You&apos;re on the waitlist.</h2>
              <p>
                Thanks. We&apos;ll use your preferred contact when a beta spot opens.
              </p>
              <Link className="btn btn-ghost" to="/">Back to KinSafe</Link>
            </div>
          ) : (
            <form className="beta-form" onSubmit={submit}>
              <div className="trap" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" tabIndex={-1} autoComplete="off" />
              </div>

              <label htmlFor="name">Your name</label>
              <input id="name" name="name" required maxLength={100} autoComplete="name" />

              <label htmlFor="contact">Phone number or Discord username</label>
              <input
                id="contact"
                name="contact"
                required
                maxLength={120}
                autoComplete="tel"
                aria-describedby="contact-help"
              />
              <small id="contact-help">No email address is required or displayed.</small>

              <label htmlFor="role">How would you use KinSafe?</label>
              <select id="role" name="role" required defaultValue="">
                <option value="" disabled>Select one</option>
                <option value="parent">Parent or guardian</option>
                <option value="accountability">Accountability partner or sponsor</option>
                <option value="self">For myself</option>
                <option value="other">Something else</option>
              </select>

              <label htmlFor="setup">What Windows computers would you test it on?</label>
              <textarea id="setup" name="setup" required maxLength={1200} rows={5} />

              <p className="form-disclosure">
                Your preferred contact and test setup are sent to KinSafe&apos;s
                private beta-team Slack channel so we can follow up about access.
              </p>
              <div ref={turnstileHost} className="turnstile-host" />
              {turnstileUnavailable && (
                <p className="form-error" role="alert">
                  Verification could not load. Check content blocking and refresh
                  this page to try again.
                </p>
              )}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={state === "submitting" || !turnstileToken}
                aria-busy={state === "submitting"}
              >
                {state === "submitting" ? "Joining…" : "Join the beta waitlist"}
              </button>
              {state === "error" && (
                <p className="form-error" role="alert">
                  We couldn&apos;t submit that just now. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
